import React, {
  ChangeEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components/macro';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { setAudioCurrentTime } from 'redux/actions/audio/audioActions';
import { AudioStatuses } from 'redux/reducers/audioReducer';
import { IRootState } from 'constants/types';
import { States } from 'constants/states';
import useDebounce from 'hooks/useDebounce';

const AudioProgressContainer = styled.div`
  width: auto;
`;
const AudioProgressInner = styled.div<{ disabled?: boolean }>`
  position: relative;
  width: 100%;
  height: 4px;
  z-index: 10;
  & > div:last-child {
    background: ${({ disabled }) => disabled && 'unset'}
  }
`;
const Progress = styled.div`
    content: "";
    position: absolute;
    left: 0;
    width: 0;
    height: 4px;
    background: ${({ theme }) => theme.palette.secondary};
    border-radius: 0 0 100px 100px;
    z-index: -10;
`;
const Input = styled.input`
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;
    height: inherit;
    background: transparent;
    -moz-appearance: none;
    -webkit-appearance: none;
    &:focus {
      outline: none;
    }
    &::-moz-range-track {
      background: transparent;
      border: 0;
      border-radius: 5px;
      width: 100%;
      height: 14px;
      -moz-appearance: none;
      cursor: pointer;
    }
    &::-webkit-slider-runnable-track {
        background: transparent;
        border: 0;
        border-radius: 5px;
        width: 100%;
        height: 14px;
        -webkit-appearance: none;
        cursor: pointer;
    }
    &::-moz-range-thumb {
      margin-top: 0;
      width: 14px;
      height: 14px;
      background: transparent;
      border: 0;
      border-radius: 50px;
      cursor: pointer;
      -moz-appearance: none;
    }
    &::-webkit-slider-thumb {
        margin-top: 0;
        width: 14px;
        height: 14px;
        background: transparent;
        border: 0;
        border-radius: 50px;
        cursor: pointer;
        -webkit-appearance: none;
    }
    @supports (-ms-ime-align:auto) {
        margin: 0;
    }
`;

interface IAudioProgress {
  disabled?: boolean
  className?: string
}

const AudioProgress = (props: IAudioProgress) => {
  const {
    disabled,
    className,
  } = props;

  const dispatch = useDispatch<Dispatch>();

  const progressRef = useRef<HTMLDivElement>(null);

  const [changeValue, setChangeValue] = useState<number | null>(null);

  const debouncedChangeValue = useDebounce<number | null>(changeValue, 500);

  const audioState = useSelector((state: IRootState) => state?.[States.AUDIO]);
  const audioTime = audioState?.time;
  const currentTime = audioTime?.current || 0;
  const totalTime = audioTime?.total || 0;
  const isStatusStop = audioState.status === AudioStatuses.STOP;

  const value = changeValue || currentTime;
  const progress: number = ((100 / (totalTime - 1)) * (value - 1)) || 0;

  useEffect(() => {
    if (progressRef.current) {
      if (totalTime !== 0) {
        progressRef.current.style.width = `${progress}%`;
      }
      if (progress <= 0) {
        progressRef.current.style.width = '0%';
      }
    }
  }, [progress]);

  useEffect(() => {
    if (progressRef.current && isStatusStop) {
      progressRef.current.style.width = '0%';
    }
  }, [isStatusStop]);

  useEffect(() => {
    if (debouncedChangeValue) {
      dispatch(setAudioCurrentTime(debouncedChangeValue));
      setChangeValue(null);
    }
  }, [debouncedChangeValue]);

  const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const newValue = +ev.target.value || 0;
    setChangeValue(newValue);
  };

  return (
    <AudioProgressContainer className={className}>
      <AudioProgressInner disabled={disabled}>
        <Input
          type="range"
          value={currentTime}
          min={1}
          disabled={disabled}
          max={totalTime}
          onChange={handleChange}
        />
        <Progress ref={progressRef} />
      </AudioProgressInner>
    </AudioProgressContainer>
  );
};

export default AudioProgress;
